import { useEffect, useRef } from 'react';
import { getScheduledPaymentGroupStatus } from '@/services/batchPayment';
import { BatchPaymentPollStatusEnum } from '@/features/payments/types/BatchPayment';
import { TPaymentUpdate } from '../utils/applyPaymentUpdateToGroups';

const POLL_INTERVAL_MS = 5000;

/**
 * Mirrors the batch-creation workflow's own polling (see pollBatchPaymentStatus in
 * features/payments/hooks/useBatchPayment.ts) for groups that are already sitting in the
 * monitoring list: any row currently IN_PROGRESS gets its own 5-second poll against the same
 * group-status endpoint, so the list picks up completion (or failure) without a page refresh.
 *
 * `groupIds` is expected to be a stable-ish array (recomputed from the payments list) - this
 * hook keys polling by id via a ref rather than the array reference, so a re-render that
 * produces a new array with the same ids doesn't restart any in-flight poll.
 */
export const useInProgressGroupPolling = (
  groupIds: string[],
  onUpdate: (patch: TPaymentUpdate) => void
) => {
  const timersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // Recomputed on every render, but only changes (as a string) when the actual set of
  // in-progress ids changes - keeps the effect below from tearing down and rebuilding timers on
  // unrelated re-renders of the payments list.
  const idsKey = groupIds.slice().sort().join(',');

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    const timers = timersRef.current;

    ids.forEach((groupId) => {
      if (timers.has(groupId)) return;

      const timer = setInterval(async () => {
        try {
          const response = await getScheduledPaymentGroupStatus(groupId);
          if (response.state) {
            onUpdateRef.current({
              id: groupId,
              state: response.state,
              completedPayments: response.completedPayments,
              failedPayments: response.failedPayments
            });
          }
          if (response.status !== BatchPaymentPollStatusEnum.IN_PROGRESS) {
            clearInterval(timer);
            timers.delete(groupId);
          }
        } catch (error) {
          console.error('Error polling in-progress batch status:', error);
        }
      }, POLL_INTERVAL_MS);

      timers.set(groupId, timer);
    });

    // Stop polling anything that's dropped out of the in-progress set (normally this happens on
    // its own inside the tick above once a poll observes a terminal status, but covers the case
    // where the row left IN_PROGRESS through some other path).
    timers.forEach((timer, groupId) => {
      if (!ids.includes(groupId)) {
        clearInterval(timer);
        timers.delete(groupId);
      }
    });
  }, [idsKey]);

  // Stop every remaining poll on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearInterval(timer));
      timers.clear();
    };
  }, []);
};
